import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    FileExcelOutlined,
    InboxOutlined,
    PlusOutlined,
    SettingOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Dropdown,
    Menu,
    Modal,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import AddAgenceModal from './AddAgenceModal';
import { rightsService } from '../../../services/rightsService';

const { Text } = Typography;
const { confirm } = Modal;

const AGENCE_TEMPLATE_HEADERS = [
    "CodeAgence",
    "NomAgence",
    "Ville",
    "Siege",
    "ResponsableAgence",
    "EmailAgence",
    "Telephone1Agence",
    "Telephone2Agence",
    "TypeAgence",
];

const AgenceTab = ({ colors, agences, membre, visible, setVisible, onAgenceSaved }) => {
    // const [visible, setVisible] = useState(false);
    const [villes, setVilles] = useState([]);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importConfirm, setImportConfirm] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [importError, setImportError] = useState(null);

    const canManageAgences = rightsService("HPE033") || rightsService("HPE041");

    useEffect(() => {
        // Charger la liste des villes pour le formulaire d'ajout d'agence
        axiosInstance
            .get('/api/parametre/villes')
            .then((res) => {
                const data = Array.isArray(res?.data) ? res.data : [];
                setVilles(data);
            })
            .catch((err) => {
                console.error("Erreur lors du chargement des villes:", err);
                setVilles([]);
            });
    }, [visible]);

    const navigate = useNavigate();

    const importRows = useMemo(() => importPreview?.rows || [], [importPreview]);

    const invalidRowsCount = useMemo(() => {
        if (!importPreview?.rows) return 0;
        if (typeof importPreview.invalidRows === "number") {
            return importPreview.invalidRows;
        }
        return importPreview.rows.filter((row) => !row.valid).length;
    }, [importPreview]);

    const importSummary = useMemo(() => {
        if (!importPreview) return null;
        const total = importPreview?.totalRows ?? importRows.length ?? 0;
        const valid = importPreview?.validRows ?? importRows.filter((row) => row.valid).length;
        const invalid = importPreview?.invalidRows ?? invalidRowsCount;
        return {
            total,
            valid,
            invalid,
        };
    }, [importPreview, importRows, invalidRowsCount]);

    const confirmSummary = useMemo(() => {
        if (!importConfirm) return null;
        return {
            total: importConfirm?.totalRows ?? importRows.length ?? 0,
            created: importConfirm?.createdRows ?? 0,
            failed: importConfirm?.failedRows ?? 0,
        };
    }, [importConfirm, importRows]);

    const isExcelFile = (file) => {
        if (!file) return false;
        return /\.xlsx?$/.test((file.name || "").toLowerCase());
    };

    const resetImportState = () => {
        setImportFile(null);
        setImportPreview(null);
        setImportConfirm(null);
        setImportError(null);
        setPreviewLoading(false);
        setConfirmLoading(false);
    };

    const handleOpenImportModal = () => {
        resetImportState();
        setImportModalOpen(true);
    };

    const handleCloseImportModal = () => {
        setImportModalOpen(false);
        resetImportState();
    };

    const handleFileSelect = (file) => {
        if (!isExcelFile(file)) {
            toast.error("Format de fichier invalide. Choisissez un fichier .xlsx ou .xls");
            return Upload.LIST_IGNORE;
        }
        setImportFile(file);
        setImportPreview(null);
        setImportConfirm(null);
        setImportError(null);
        return false;
    };

    const handlePreviewImport = async () => {
        if (!importFile) return;
        setPreviewLoading(true);
        setImportError(null);
        setImportConfirm(null);
        const formData = new FormData();
        formData.append("file", importFile);
        try {
            const res = await axiosInstance.post("/api/agences/import/preview", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportPreview(res.data);
            toast.success("Prévisualisation effectuée");
        } catch (error) {
            console.log(error);
            const message =
                error?.response?.data?.message ||
                "Erreur lors de la prévisualisation. Vérifiez les en-têtes ou le contenu du fichier.";
            setImportError(message);
            toast.error(message);
        }
        setPreviewLoading(false);
    };

    const handleConfirmImport = async () => {
        if (!importFile || !importPreview || invalidRowsCount > 0) return;
        setConfirmLoading(true);
        setImportError(null);
        const formData = new FormData();
        formData.append("file", importFile);
        try {
            const res = await axiosInstance.post("/api/agences/import/confirm", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportConfirm(res.data);
            setImportPreview(res.data);
            toast.success("Import confirmé");
        } catch (error) {
            console.log(error);
            const message =
                error?.response?.data?.message ||
                "Erreur lors de la confirmation. Veuillez vérifier le fichier puis réessayer.";
            setImportError(message);
            toast.error(message);
        }
        setConfirmLoading(false);
    };

    const handleTemplateDownload = async () => {
        try {
            const ExcelJS = (await import("exceljs")).default;
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Agences");
            sheet.columns = AGENCE_TEMPLATE_HEADERS.map((header) => ({
                header,
                key: header,
                width: Math.max(header.length + 4, 15),
            }));
            sheet.addRow([
                "AG001",
                "Agence Centrale",
                "Lome",
                "OUI",
                "Jane Doe",
                "agence@example.com",
                "90112233",
                "99112233",
                "AGENCE",
            ]);
            sheet.getRow(1).font = { bold: true };
            sheet.getRow(2).font = { italic: true };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "template_import_agences.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.log(error);
            toast.error("Impossible de télécharger le template");
        }
    };

    const renderImportStatus = (row) => {
        const hasErrors = Array.isArray(row?.errors) && row.errors.length > 0;
        const createdCode = row?.createdAgenceCodeId || row?.createdCodeId || row?.codeId;
        if (importConfirm) {
            const created = !!createdCode && !hasErrors;
            return (
                <Tag color={created ? "green" : "red"} icon={created ? <CheckCircleFilled /> : <CloseCircleFilled />}>
                    {created ? "Créée" : "Erreur"}
                </Tag>
            );
        }
        const ok = row?.valid && !hasErrors;
        return (
            <Tag color={ok ? "green" : "red"} icon={ok ? <CheckCircleFilled /> : <CloseCircleFilled />}>
                {ok ? "Valide" : "Erreur"}
            </Tag>
        );
    };

    const importColumns = [
        {
            title: "#",
            dataIndex: "rowNumber",
            key: "rowNumber",
            width: 70,
        },
        {
            title: "Code agence",
            dataIndex: "codeAgence",
            key: "codeAgence",
        },
        {
            title: "Nom",
            dataIndex: "nomAgence",
            key: "nomAgence",
            render: (value, record) => value || record?.nom || record?.agence || "-",
        },
        {
            title: "Ville",
            dataIndex: "ville",
            key: "ville",
        },
        {
            title: "Siège",
            dataIndex: "siege",
            key: "siege",
            render: (value) => {
                const normalized = typeof value === "string" ? value.toLowerCase() : value;
                const isYes = normalized === true || normalized === "oui" || normalized === "true";
                if (value === undefined || value === null) return "-";
                return <Tag color={isYes ? "blue" : "default"}>{isYes ? "Oui" : "Non"}</Tag>;
            },
        },
        {
            title: "Email",
            dataIndex: "emailAgence",
            key: "emailAgence",
        },
        {
            title: "Responsable",
            dataIndex: "responsableAgence",
            key: "responsableAgence",
            render: (val, record) => val || record?.responsable || "-",
        },
        {
            title: "Téléphone",
            dataIndex: "telephone1Agence",
            key: "telephone1Agence",
            render: (val, record) => {
                const tel1 = val || record?.telephone1 || record?.telephone || (Array.isArray(record?.contact) ? record.contact[0] : undefined);
                const tel2 = record?.telephone2Agence || record?.telephone2 || (Array.isArray(record?.contact) ? record.contact[1] : undefined);
                if (tel1 && tel2) return `${tel1} / ${tel2}`;
                return tel1 || tel2 || "-";
            },
        },
        {
            title: "Téléphone 2",
            dataIndex: "telephone2Agence",
            key: "telephone2Agence",
            render: (val, record) => val || record?.telephone2 || (Array.isArray(record?.contact) ? record.contact[1] : "-") || "-",
        },
        {
            title: "Type",
            dataIndex: "typeAgence",
            key: "typeAgence",
        },
        {
            title: "Statut",
            key: "status",
            render: (_, record) => renderImportStatus(record),
        },
        {
            title: "Erreurs",
            dataIndex: "errors",
            key: "errors",
            render: (errors) =>
                errors && errors.length ? (
                    <Tooltip title={errors.join(", ")}>
                        <span>{errors.join(" | ")}</span>
                    </Tooltip>
                ) : (
                    <Tag color="green">Aucune</Tag>
                ),
        },
        {
            title: "Code créé",
            dataIndex: "createdAgenceCodeId",
            key: "createdAgenceCodeId",
            render: (_, record) => record?.createdAgenceCodeId || record?.createdCodeId || record?.codeId || "-",
        },
    ];

    const canConfirmImport = !!(
        importPreview &&
        invalidRowsCount === 0 &&
        importRows.length &&
        !previewLoading
    );

    const showActivateConfirm = (agence) => {
        confirm({
            title: `Etes-vous sûr de vouloir ${agence?.restrictionProduction ? "activer" : "désactiver"} cette agence?`,
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées!",
            okText: "Oui",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                activateAgency(agence);
            },
        });
    };

    const activateAgency = (agence) => {
        axiosInstance
            .get(`/api/agence/activate/${agence.codeId}`)
            .then((res) => {
                window.location.reload();
                toast(
                    agence?.restrictionProduction
                        ? "Agence activée avec succès"
                        : "Agence désactivée avec succès",
                );
            })
            .catch((err) => {
                console.log(err);
                toast("Erreur lors de la mise à jour");
            });
    };
    const columnsAgences = [
        {
            title: 'Code',
            dataIndex: 'codeAgence',
            key: 'codeAgence'
        },
        {
            title: 'Agence',
            dataIndex: 'nom',
            key: 'nom'
        },
        {
            title: 'Type',
            dataIndex: 'typeAgence',
            key: 'typeAgence'
        },
        {
            title: "Adresse",
            dataIndex: "ville",
            key: "ville",
        },
        {
            title: "Responable",
            dataIndex: "responsable",
            key: "responsable",
        },
        {
            title: "Statut",
            dataIndex: "statut",
            key: "statut",
            render: (_, record) => (
                <>
                    {!record.restrictionProduction ? <Tag color="green">Actif</Tag> : <Tag color="red">Inactif</Tag>}
                </>
            )
        },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => {
                const items = [
                    {
                        key: "details",
                        label: "Détails",
                        onClick: () => navigate(`/agence`, { state: { codeAgence: record.codeAgence, codeOrganisme: membre?.code } }),
                    },
                ];

                if (canManageAgences) {
                    items.push({
                        key: "toggle",
                        label: record?.restrictionProduction ? "Activer" : "Désactiver",
                        onClick: () => showActivateConfirm(record),
                    });
                }

                return (
                    <Dropdown overlay={<Menu items={items} />}>
                        <Button icon={<SettingOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];
    return (
        <div>
            <Space wrap style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{
                        backgroundColor: colors.primary,
                        borderColor: colors.primary,
                        borderRadius: '8px'
                    }}
                    onClick={() => setVisible(true)}
                >
                    Ajouter une nouvelle agence
                </Button>
                {canManageAgences && (
                    <Button
                        icon={<UploadOutlined />}
                        size="large"
                        onClick={handleOpenImportModal}
                        style={{
                            borderRadius: '8px',
                            borderColor: colors.primary,
                            color: colors.primary
                        }}
                    >
                        Importer (Excel)
                    </Button>
                )}
            </Space>
            <Card style={{ borderRadius: '8px', marginTop: '16px' }}>
                {agences?.length > 0 ? <Table columns={columnsAgences} dataSource={agences} pagination={{ showSizeChanger: true }} /> : <Text type="secondary">Liste des agences à venir...</Text>}
            </Card>

            <AddAgenceModal
                visible={visible}
                setVisible={setVisible}
                onCancel={() => setVisible(false)}
                villes={villes}
                membre={membre}
                onSaved={onAgenceSaved}
            />

            <Modal
                title="Import des agences (Excel)"
                open={importModalOpen}
                onCancel={handleCloseImportModal}
                width={1000}
                destroyOnClose
                footer={[
                    <Button key="template" icon={<FileExcelOutlined />} onClick={handleTemplateDownload}>
                        Télécharger un template
                    </Button>,
                    <Button
                        key="preview"
                        type="primary"
                        onClick={handlePreviewImport}
                        disabled={!importFile}
                        loading={previewLoading}
                    >
                        Prévisualiser
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        danger
                        onClick={handleConfirmImport}
                        disabled={!canConfirmImport}
                        loading={confirmLoading}
                    >
                        Confirmer l'import
                    </Button>,
                ]}
            >
                <Alert
                    message={
                        <Space direction="vertical">
                            <span>En-têtes attendues : CodeAgence, NomAgence, Ville, Siege, ResponsableAgence, EmailAgence, Telephone1Agence, Telephone2Agence, TypeAgence.</span>
                            <span>Une ligne = une agence. L'organisme est déduit automatiquement de l'utilisateur connecté.</span>
                        </Space>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                />
                <Upload.Dragger
                    multiple={false}
                    accept=".xlsx,.xls"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                    disabled={previewLoading || confirmLoading}
                    style={{ borderRadius: "12px" }}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Déposez le fichier ici ou cliquez pour sélectionner
                    </p>
                    <p className="ant-upload-hint">
                        Extensions acceptées : .xlsx, .xls
                    </p>
                </Upload.Dragger>
                {importFile ? (
                    <div style={{ marginTop: 8 }}>
                        <Tag color="blue">{importFile.name}</Tag>
                    </div>
                ) : null}

                {importError ? (
                    <Alert
                        style={{ marginTop: 12 }}
                        message={importError}
                        type="error"
                        showIcon
                    />
                ) : null}

                {importSummary ? (
                    <div style={{ marginTop: 12 }}>
                        <Space wrap>
                            <Tag color="geekblue">Total : {importSummary.total}</Tag>
                            <Tag color="green">Valides : {importSummary.valid}</Tag>
                            <Tag color="red">Invalides : {importSummary.invalid}</Tag>
                            {confirmSummary ? (
                                <>
                                    <Tag color="green">Créées : {confirmSummary.created}</Tag>
                                    <Tag color="red">Échouées : {confirmSummary.failed}</Tag>
                                </>
                            ) : null}
                        </Space>
                    </div>
                ) : null}

                {importSummary?.invalid > 0 ? (
                    <Alert
                        style={{ marginTop: 12 }}
                        message="Certaines lignes contiennent des erreurs, corrigez le fichier puis relancez la prévisualisation."
                        type="warning"
                        showIcon
                    />
                ) : null}

                {importPreview ? (
                    <Table
                        style={{ marginTop: 16 }}
                        columns={importColumns}
                        dataSource={importRows}
                        rowKey={(record) => record?.rowNumber || `${record.codeAgence}-${record.nomAgence}-${record.rowNumber}`}
                        pagination={{ pageSize: 8 }}
                        scroll={{ x: true }}
                    />
                ) : null}
            </Modal>
        </div>
    )
}

export default AgenceTab
